
import { CreateElement } from "vue";
import Component, { mixins } from "vue-class-component";
import { ProvideReactive } from "vue-property-decorator";
import { CallPrice } from "@scrile/api-provider/dist/api/CallPriceProvider";
import { CallCreateResponse, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { PaidCallType } from "@scrile/api-provider/dist/api/PaidCallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { getMainCurrencyCode } from "@/lib/moneyHelpers";
import { callMethod, callRenderFunction } from "@/lib/vueClassHelpers";
import { NotificationData } from "@/types";
import { ERRORS, EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import CallController from "@/components/CallController/CallController.ts";
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
// noinspection TypeScriptCheckImport
import CallControllerWithRender from "@/components/CallController?original=true";
import PaidActionConfirm from "@/components/PaidActionConfirm";
import PaidActionError from "@/components/PaidActionError";
import WithPaidCalls from "@/plugins/paidCalls/mixins/WithPaidCalls";

export interface CallPrices {
  currentCallPrice: number;
  videoCallPrice: number;
}

// inject: [] - temporary bug fix for ProvideReactive: https://github.com/kaorun343/vue-property-decorator/issues/277
@Component({ name: "PaidCallController", components: { BaseConfirm, BaseButton }, inject: [] })
export default class PaidCallController extends mixins(CallController, WithPaidCalls, WithAuthUser) {
  public showPaidConfirm = false;
  public showPaidVideoSwitchConfirm = false;
  public resolvePending: ((v: boolean) => void) | undefined;
  public showRefillBalance = false;
  public showRefillBalanceImportant = false;
  public callPrices: CallPrice | null = null;
  public callType: PaidCallType = PaidCallType.AUDIO;

  public get audioCallPrice() {
    return (
      this.callPrices?.perMinuteAudioPricePackage?.costs.find(i => i.currency === getMainCurrencyCode())?.cost || 0
    );
  }

  public get videoCallPrice() {
    return (
      this.callPrices?.perMinuteVideoPricePackage?.costs.find(i => i.currency === getMainCurrencyCode())?.cost || 0
    );
  }

  public get currentCallPrice() {
    return this.callType === PaidCallType.AUDIO ? this.audioCallPrice : this.videoCallPrice;
  }

  @ProvideReactive("callPriceToProvide")
  public get callPriceToProvide() {
    return {
      currentCallPrice: this.currentCallPrice,
      videoCallPrice: this.videoCallPrice,
    };
  }

  public onVideoSwitch(v: boolean) {
    this.showPaidVideoSwitchConfirm = false;
    this.resolvePending && this.resolvePending(v);
  }

  @ProvideReactive("paidCallRequestData")
  public get paidCallRequestData() {
    return {
      show: this.showPaidVideoSwitchConfirm,
      on: this.onVideoSwitch,
    };
  }
  public refillBalance() {
    this.showRefillBalanceImportant = false;
    emitter.emit(EVENTS.BALANCE_REFILL_SHOW);
  }

  public callCreate(data: { receiverId: string }) {
    providers.PaidCallsProvider.setReceiveErrors(false);
    const callPromise = callMethod(this, CallController, "callCreate", [data]) as Promise<CallCreateResponse>;
    callPromise.catch((e: ErrorDTO[]) =>
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === ERRORS.PAID_CALL_CREATE,
          onSuccessCB: () => (this.showRefillBalanceImportant = true),
        },
      ])
    );
    return callPromise;
  }

  public setPaidChatAnswer(status: boolean) {
    this.showPaidConfirm = false;
    if (status) {
      callMethod(this, CallController, "callOutgoingHandler", [{ opponentUser: this.opponentUser }]);
    } else {
      this.reset(true, true);
    }
  }

  public async getCallPrice(userId: string) {
    this.callPrices = await this.paidCallsFindCallPrice(userId);
  }

  public async setVideoState(status: boolean) {
    if (!this.call) return;
    let newCallType = status ? PaidCallType.VIDEO : PaidCallType.AUDIO;
    if (newCallType === PaidCallType.VIDEO) {
      try {
        await callMethod(this, CallController, "getDevicePermission", [{ video: true }]);
      } catch (error) {
        await callMethod(this, CallController, "collectErrorLogs", [error]);
        callMethod(this, CallController, "setVideoState", [false]);
        // eslint-disable-next-line no-console
        console.warn(error);
        return;
      }
    }
    if (this.call.callerId === this.authUserStrict.id) {
      if (status && this.videoCallPrice > 0 && !this.opponentReadyVideoState) {
        this.showPaidVideoSwitchConfirm = true;
        const { promise, resolve } = createDeferred<boolean>();
        this.resolvePending = resolve;
        const userResponse = await promise;
        if (!userResponse) return;
      }
      if (this.audioCallPrice > 0 || this.videoCallPrice > 0) {
        try {
          providers.PaidCallsProvider.setReceiveErrors(false);
          await this.updatePaidCall(this.call.id, newCallType);
        } catch (e) {
          handleRequestError(e, [
            {
              comparator: e => e.details?.messageTemplate === ERRORS.SPENDING_BALANCE,
              onSuccessCB: () => (this.showRefillBalance = true),
            },
          ]);
        }
      }
      if (this.showRefillBalance) return;
      this.callType = newCallType;
    }

    callMethod(this, CallController, "setVideoState", [status]);
  }

  public async callOutgoingHandler({ opponentUser }: { opponentUser: UserPublicData }) {
    await this.getCallPrice(opponentUser.id);
    if (this.currentCallPrice === 0) {
      callMethod(this, CallController, "callOutgoingHandler", [{ opponentUser }]);
    } else {
      this.opponentUser = opponentUser;
      this.showPaidConfirm = true;
    }
  }

  public async callIncomingHandler(...args: any[]) {
    if (this.call) return;
    await this.getCallPrice(this.authUserStrict.id);
    return callMethod(this, CallController, "callIncomingHandler", args);
  }

  public render(h: CreateElement) {
    if (this.showPaidConfirm) {
      return h(PaidActionConfirm, {
        props: { value: this.showPaidConfirm, price: this.currentCallPrice },
        on: { input: this.setPaidChatAnswer },
        scopedSlots: {
          default: ({ price }: { price: string }) => $t("Call for {0} per min", [price]),
        },
      });
    }
    return h("div", { staticStyle: { position: "absolute" } }, [
      callRenderFunction(this, CallControllerWithRender, h),
      h(PaidActionError, {
        props: { value: this.showRefillBalanceImportant },
        on: {
          input: (v: boolean) => {
            this.showRefillBalanceImportant = v;
            if (this.call?.status !== CallStatus.NEW || this.call?.callerId === this.authUserStrict.id) {
              this.reset(true, !(this.call && [CallStatus.ACCEPTED, CallStatus.NEW].includes(this.call.status)));
            }
          },
        },
      }),
      h(PaidActionError, {
        props: { value: this.showRefillBalance },
        on: {
          input: (v: boolean) => (this.showRefillBalance = v),
        },
      }),
    ]);
  }

  public reset(...args: any[]) {
    this.callPrices = null;
    this.callType = PaidCallType.AUDIO;
    return callMethod(this, CallController, "reset", args);
  }

  public async created() {
    const paidCallEventListener = await this.paidCallsSubscribeRequestPaymentForCall(async callId => {
      if (this.call && callId === this.call.id) {
        const notification: NotificationData = {
          text: $t("1 min left! Please refill"),
          position: "top",
          timeout: 60 * 1000,
          onNotificationClick: () => this.refillBalance(),
          button: {
            text: $t("Close"),
          },
        };
        emitter.emit(EVENTS.NOTIFICATION_SHOW, notification);
      }
    });
    this.$once("hook:beforeDestroy", () => paidCallEventListener.unsubscribe());
  }
}
